import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useRef } from 'react';
import cn from 'classnames';
import { Controller, useFormContext } from 'react-hook-form';
import { richTextValidation } from 'validators';
import { Column, GradientIcon, RichTextEditor, TextButton, Typography, useDevice, useIsInBottomSheet } from 'seagull';
import { EditAI } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { DefaultValuePopover } from '~/components/AgreementStudioPage/TemplateVariables/DefaultValuePopover/DefaultValuePopover';
import { useRichTextVariableIntegration } from '~/components/AgreementStudioPage/TemplateVariables/hooks/useRichTextVariableIntegration';
import { VariableInsertButton } from '~/components/AgreementStudioPage/TemplateVariables/VariableInsertButton/VariableInsertButton';
import { VariablePickerPopover } from '~/components/VariablePickerPopover/VariablePickerPopover';
import { useLocaleKeys } from '~/i18n';
import { connectControllerToRichTextEditor } from '~/utils/react-hook-form.utils';
import styles from './ServiceDescription.module.scss';
export const GetServiceDescriptionTID = (parentTestId)=>getTestIds(parentTestId);
const ServiceDescription = (param)=>{
    let { testId, hideLabel = false, onChange = ()=>{}, onHelpMeWriteClick, disabledHelpMeWrite = false, isDrawerOpen = false, disabled = false, disableVariablePicker } = param;
    const { t: descriptionTexts } = useLocaleKeys('serviceBuilder.description');
    const { isMobileDevice } = useDevice();
    const isInBottomSheet = useIsInBottomSheet();
    const hideHelpMeWrite = isInBottomSheet || isMobileDevice;
    const showMobileCard = isMobileDevice && !isInBottomSheet;
    const { control, watch } = useFormContext();
    const isBundle = Boolean(watch('bundle'));
    const label = isBundle ? descriptionTexts.bundleLabel() : descriptionTexts.serviceLabel();
    const { ServiceDescriptionTestIds } = useMemo(()=>GetServiceDescriptionTID(testId), [
        testId
    ]);
    const editorElRef = useRef(null);
    const { enabled: variablesEnabled, toolbarButtonProps, pickerPopoverProps, defaultValuePopoverProps } = useRichTextVariableIntegration(editorElRef, {
        disabled,
        disableVariablePicker
    });
    const isOnlyDisabledHelpMeWrite = !isDrawerOpen && disabledHelpMeWrite;
    const helpMeWriteButton = !!onHelpMeWriteClick && !hideHelpMeWrite ? /*#__PURE__*/ _jsx(TextButton, {
        bi: {
            name: 'help_me_write_service_description'
        },
        tooltip: isOnlyDisabledHelpMeWrite ? {
            title: descriptionTexts.helpMeWriteTooltip(),
            placement: 'top'
        } : undefined,
        className: styles.helpMeWriteButton,
        prefixIcon: /*#__PURE__*/ _jsx(GradientIcon, {
            icon: /*#__PURE__*/ _jsx(EditAI, {})
        }),
        hoverSkin: isDrawerOpen ? undefined : 'magical',
        underline: "none",
        weight: "normal",
        skin: isDrawerOpen ? 'focusPrimary' : 'primary',
        disabled: isOnlyDisabledHelpMeWrite,
        onClick: onHelpMeWriteClick,
        testId: ServiceDescriptionTestIds.HelpMeWriteButton(),
        children: descriptionTexts.helpMeWrite()
    }) : null;
    const variableButton = variablesEnabled ? /*#__PURE__*/ _jsx(VariableInsertButton, _object_spread({}, toolbarButtonProps)) : null;
    const toolbarActions = /*#__PURE__*/ _jsxs("div", {
        className: styles.toolbarActionsRow,
        children: [
            variableButton,
            helpMeWriteButton
        ]
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Column, {
                gap: 8,
                alignItems: "start",
                className: cn({
                    [styles.mobile]: showMobileCard
                }),
                children: [
                    !hideLabel && !showMobileCard && /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        skin: "secondary",
                        testId: "".concat(testId, "-label"),
                        children: label
                    }),
                    /*#__PURE__*/ _jsx(Controller, {
                        control: control,
                        name: "description",
                        rules: richTextValidation(),
                        render: (renderProps)=>{
                            const connected = connectControllerToRichTextEditor(renderProps);
                            const originalEditorRef = connected.editorRef;
                            return /*#__PURE__*/ _jsx(RichTextEditor, _object_spread_props(_object_spread({
                                bi: {
                                    name: 'service_description'
                                },
                                editorHeight: 302,
                                placeholder: descriptionTexts.placeholder(),
                                testId: testId
                            }, connected), {
                                editorRef: (el)=>{
                                    editorElRef.current = el;
                                    if (typeof originalEditorRef === 'function') {
                                        originalEditorRef(el);
                                    }
                                },
                                onChange: (v)=>{
                                    onChange(v);
                                    connected.onChange(v);
                                },
                                toolbarActions: toolbarActions,
                                disabled: disabled,
                                mobile: {
                                    renderAsCard: true,
                                    summaryLabel: label
                                }
                            }));
                        }
                    })
                ]
            }),
            variablesEnabled && /*#__PURE__*/ _jsx(VariablePickerPopover, _object_spread({}, pickerPopoverProps)),
            variablesEnabled && /*#__PURE__*/ _jsx(DefaultValuePopover, _object_spread({}, defaultValuePopoverProps))
        ]
    });
};
export default ServiceDescription;
